// ═══════════════════════════════════════════════════════════════════════════
// grainHarvestExecution.js — HARVEST OUTLET REQUIRED vs. STORAGE OPTIONALITY
// (Kevin, 2026-08-19 final acceptance correction: "storage must affect
// harvest exposure/action, not only display... Introduce a separate,
// explicit HARVEST EXECUTION / OPTIONALITY output derived only from known
// storage.exposure + known physical commitment/expected crop. It must
// answer, in bushels, how much production requires a harvest outlet versus
// how much can remain optional in storage.").
//
// This is a LOGISTICS/EXPOSURE claim — how many bushels have no choice but to
// leave the farm at harvest (storage-constrained) versus how many can wait —
// NEVER a market/pricing claim and NEVER a destination/buyer instruction.
// It reads grainStorage.js's deriveStorageExposure() output (the
// expectedCropBu-vs-storageCapacityBu split) and grainCommitment.js's
// physicalBu (delivery-obligation bushels, from ANY contract type,
// REGARDLESS of price) — deliberately NOT priceProtectedBu, per Kevin's
// explicit instruction: "use the existing commitment.physicalBu field for
// outlet coverage, not priceProtectedBu." An HTA contract is physically
// committed AND price-protected; a Bushels Only contract is physically
// committed but NOT price-protected — either one already answers "does this
// bushel have somewhere to go," which is the only question this file asks.
//
// It reads NOTHING that feeds status/marketOpportunityState/suggestedBu/
// targetPosition (all computed from percentile/regime/contract position,
// never from storage), and nothing above reads it back — a caller can drop
// this field entirely without changing any other output on
// buildGrainAction()'s return object, the same passthrough contract
// storage.exposure and regime already follow.
//
// UNKNOWN NEVER BECOMES ZERO: either input being unresolved (storage mode/
// capacity/expected-crop not known, OR physical commitment unknown because
// zero grain_contracts rows exist on file for this crop/year) returns
// known:false with every bushel figure withheld — never a fabricated "0 bu
// gap" that would read as "nothing needs an outlet."
//
// PURE — no fetch, no React, no Supabase.
// ═══════════════════════════════════════════════════════════════════════════

import { pctOfExpected } from "./grainCommitment";

const UNKNOWN_HARVEST_EXECUTION = (reason) => ({
  known: false, reason,
  harvestOutletRequiredBu: null, harvestOutletCoveredBu: null, harvestOutletGapBu: null,
  storageOptionalityBu: null, harvestOutletGapPct: null, storageOptionalityPct: null,
});

// resolveHarvestExecution({ storageExposure, physicalBu, expectedBu })
//   storageExposure — deriveStorageExposure() output (grainStorage.js): the
//     expectedCropBu-vs-storageCapacityBu split. Must be known:true (a real
//     mode + — for HAS_STORAGE — a real capacity) or this withholds.
//   physicalBu — grainCommitment.js's summarizeGrainCommitment().physicalBu
//     (total delivery-obligation bushels; null when contract completeness is
//     unknown for this crop/year — the SAME unknown-vs-zero signal
//     contractDataUnknown follows elsewhere in this file family).
//   expectedBu — for percentage display only; withheld figures stay withheld
//     regardless of whether this is known.
export function resolveHarvestExecution({ storageExposure, physicalBu, expectedBu } = {}) {
  if (!storageExposure || !storageExposure.known) {
    return UNKNOWN_HARVEST_EXECUTION("storage capacity/mode or expected crop not known");
  }
  if (physicalBu == null) {
    return UNKNOWN_HARVEST_EXECUTION("physical commitment (contract completeness) unknown for this crop/year");
  }
  const harvestOutletRequiredBu = storageExposure.harvestDeliveryRequiredBu;
  const storageOptionalityBu = storageExposure.storableBu;
  // physicalBu covers up to harvestOutletRequiredBu of the requirement, and
  // no further: bushels physically committed beyond the outlet-required
  // amount are covering STORABLE bushels the farm chose to pre-sell early —
  // that's real, but it does not create a negative gap or "extra credit"
  // against bushels that were never required to move in the first place.
  const harvestOutletCoveredBu = Math.min(Math.max(0, physicalBu), harvestOutletRequiredBu);
  const harvestOutletGapBu = Math.max(0, harvestOutletRequiredBu - harvestOutletCoveredBu);
  return {
    known: true, reason: null,
    harvestOutletRequiredBu, harvestOutletCoveredBu, harvestOutletGapBu,
    storageOptionalityBu,
    harvestOutletGapPct: pctOfExpected(harvestOutletGapBu, expectedBu),
    storageOptionalityPct: pctOfExpected(storageOptionalityBu, expectedBu),
  };
}
